// A20 (08/10) — lire l'arborescence SERVIE par `/meta` : thématique d'une section, thématique
// active, redirection d'une section déplacée. (Le rangement des thématiques en familles a
// quitté l'interface le 14/10/2026, décision de Marc et Cyrille : la barre les liste à plat.)
//
// La règle qui fonde tout ce fichier (CDC A20, R1 et R2) : une section garde son identifiant
// quand elle change de thématique — `log-14` (Documents d'urbanisme) vit désormais dans `urb`.
// La thématique d'une section se lit donc dans le SOMMAIRE servi, jamais dans le préfixe de
// son identifiant. Et le sommaire suit le drapeau `ARBORESCENCE_V2` du serveur : drapeau
// éteint, `log-14` est de nouveau dans `log`, et ces fonctions le disent sans rien changer.
import type { ThemeMeta } from './types'

/** La thématique qui PORTE une section dans le sommaire servi — `null` si aucune. */
export function themeDeSection(themes: ThemeMeta[] | null | undefined,
                               sectionId: string | null | undefined): string | null {
  if (!themes || !sectionId) return null
  for (const t of themes) if (t.sections?.some(s => s.id === sectionId)) return t.id
  return null
}

/**
 * La thématique de l'URL (`…/t/<id>`), segment EXACT. L'ancien test
 * `pathname.includes('/t/' + id)` ne tenait que tant que tous les identifiants avaient la
 * même longueur : un identifiant préfixe d'un autre aurait allumé deux entrées à la fois.
 */
export function themeDeChemin(pathname: string): string | null {
  const m = pathname.match(/\/t\/([^/?#]+)/)
  if (!m) return null
  try { return decodeURIComponent(m[1]) } catch { return m[1] }
}

export const estThemeActif = (pathname: string, id: string): boolean => themeDeChemin(pathname) === id

/** L'identifiant de section visé par un `#hash` d'URL (`#log-14` → `log-14`). */
export function sectionDuHash(hash: string | null | undefined): string | null {
  if (!hash || hash.length < 2) return null
  const brut = hash.startsWith('#') ? hash.slice(1) : hash
  try { return decodeURIComponent(brut) } catch { return brut }
}

/**
 * R7 / D13 / D8 — les sections RETIRÉES de l'arborescence et la section qui reprend leur contenu.
 * `soc-1` (Revenus, pauvreté et inégalités) doublonnait `rev-1` : un partage ou un commentaire
 * ancré dessus doit rouvrir sur `rev-1` ; `log-6` (Performance énergétique, DPE) doublonnait
 * `ene-2` (même table DPE, D8 du 08/10) et rouvre sur elle. Consultée SEULEMENT quand le
 * sommaire servi ne porte plus la section — drapeau éteint, `soc-1` et `log-6` sont servies
 * et rien ne change.
 */
export const SECTIONS_REMPLACEES: Readonly<Record<string, string>> = {
  'soc-1': 'rev-1', 'log-6': 'ene-2',
}

export interface CibleAncre { theme: string; section: string }

/**
 * R4 — une adresse qui vise une section DÉPLACÉE (`/t/log#log-14` : partage, commentaire,
 * favori, marque-page) doit mener à la thématique qui la porte aujourd'hui (`/t/urb#log-14`).
 * Rend la thématique (et la section, qui ne change que pour une section remplacée), ou `null`
 * s'il n'y a rien à faire : pas d'ancre, ancre qui n'est pas une section (bloc, graphique),
 * section déjà sur cette page, ou section qu'aucune thématique visible ne porte (hors des
 * droits du compte) — l'écran garde alors son comportement d'avant.
 */
export function cibleDeAncre(themes: ThemeMeta[] | null | undefined, themeId: string | undefined,
                             hash: string | null | undefined): CibleAncre | null {
  const demandee = sectionDuHash(hash)
  if (!themes || !demandee) return null
  const ici = themes.find(t => t.id === themeId)
  if (ici?.sections?.some(s => s.id === demandee)) return null
  let section = demandee
  let theme = themeDeSection(themes, section)
  if (!theme && SECTIONS_REMPLACEES[demandee]) {
    section = SECTIONS_REMPLACEES[demandee]
    theme = themeDeSection(themes, section)
  }
  if (!theme || (theme === themeId && section === demandee)) return null
  return { theme, section }
}

/** Raccourci : la thématique cible seule (voir `cibleDeAncre`). */
export function themeCibleDeAncre(themes: ThemeMeta[] | null | undefined, themeId: string | undefined,
                                  hash: string | null | undefined): string | null {
  return cibleDeAncre(themes, themeId, hash)?.theme ?? null
}

/**
 * Le chemin d'une ancre STOCKÉE (commentaire, favori, partage) : la thématique qui porte la
 * section dans le sommaire servi d'abord, la page stockée ensuite. Sans sommaire (barre
 * « Mes commentaires » de la boîte de l'équipe), le chemin stocké mène quand même au bon
 * endroit : la page de thématique redirige d'elle-même (`cibleDeAncre`).
 */
export function cheminAncre(insee: string, page: string, section: string | null | undefined,
                            themes?: ThemeMeta[] | null): string {
  const theme = themeDeSection(themes, section) ?? page
  return `/o/${insee}/t/${theme}${section ? `#${section}` : ''}`
}
